// components/MemberProfilePanel.jsx
// Slide-in profile panel for team members. Opened by clicking a row in OrgTable or MemberSection.
// View mode by default; Edit button enables inline editing.

import React, { useState, useEffect, useCallback } from 'react';
import { X, Edit2, Check, AlertCircle, Shield, Phone, Mail, Briefcase, Calendar, User } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';
const tok = () => localStorage.getItem('token');

export default function MemberProfilePanel({ memberId, allManagers = [], onClose, onSaved, onResetPassword }) {
  const [member,  setMember]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [form,    setForm]    = useState({});

  const fetch_ = useCallback(async () => {
    if (!memberId) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/broker/team/${memberId}`, {
        headers: { Authorization: `Bearer ${tok()}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setMember(data);
      setForm({
        name:      data.name      || '',
        email:     data.email     || '',
        phone:     data.phone || data.phone_number || '',
        job_title: data.job_title || '',
        bio:       data.bio       || '',
      });
    } catch (e) { setError('Could not load profile'); }
    finally { setLoading(false); }
  }, [memberId]);

  useEffect(() => { fetch_(); }, [fetch_]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API}/broker/team/${memberId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({
          name:        form.name,
          email:       form.email,
          phone:       form.phone,
          phone_number: form.phone,
          job_title:   form.job_title,
          bio:         form.bio,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || 'Save failed');
      // Update local state with returned user
      setMember(m => ({ ...m, ...data.user }));
      setEditing(false);
      setSuccess('Saved');
      setTimeout(() => setSuccess(''), 3000);
      if (onSaved) onSaved(data.user);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const cancelEdit = () => {
    setEditing(false); setError('');
    setForm({
      name:      member.name      || '',
      email:     member.email     || '',
      phone:     member.phone || member.phone_number || '',
      job_title: member.job_title || '',
      bio:       member.bio       || '',
    });
  };

  const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 900 }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100vh', width: 440,
        maxWidth: '100vw', background: '#fff', zIndex: 901,
        boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #EDF2F7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>Team Member</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096', padding: 4, display: 'flex', alignItems: 'center' }}>
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#718096', fontSize: 14 }}>Loading…</div>
        ) : !member ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#991b1b', fontSize: 14 }}>{error || 'Not found'}</div>
        ) : (
          <div style={{ padding: '24px', flex: 1 }}>

            {/* Alerts */}
            {error && (
              <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#fee2e2', borderRadius: 8, marginBottom: 16, color: '#991b1b', fontSize: 13, alignItems: 'center' }}>
                <AlertCircle size={14} /> {error}
              </div>
            )}
            {success && (
              <div style={{ padding: '10px 14px', background: '#d1fae5', borderRadius: 8, marginBottom: 16, color: '#065f46', fontSize: 13, fontWeight: 500 }}>
                {success}
              </div>
            )}

            {/* Avatar + name block */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#0e1b2c', color: '#10b981',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 20, flexShrink: 0,
              }}>
                {initials(member.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {editing ? (
                  <input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    style={inputStyle}
                    placeholder="Name"
                    autoFocus
                  />
                ) : (
                  <div style={{ fontWeight: 700, fontSize: 17, color: '#0e1b2c' }}>{member.name}</div>
                )}
                <div style={{ marginTop: 4 }}>
                  <StatusBadge status={member.status} />
                  <RoleBadge level={member.broker_level} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {editing ? (
                  <>
                    <PanelBtn onClick={cancelEdit} bg="#EDF2F7" color="#4a5568">Cancel</PanelBtn>
                    <PanelBtn onClick={handleSave} bg="#10b981" color="#fff" disabled={saving}>
                      {saving ? 'Saving…' : <><Check size={13} style={{ marginRight: 3 }} />Save</>}
                    </PanelBtn>
                  </>
                ) : (
                  <PanelBtn onClick={() => setEditing(true)} bg="#EDF2F7" color="#374151">
                    <Edit2 size={13} style={{ marginRight: 3 }} />Edit
                  </PanelBtn>
                )}
              </div>
            </div>

            {/* Job title */}
            <Section icon={<Briefcase size={14} />} label="Job title">
              {editing ? (
                <input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))}
                  style={inputStyle} placeholder="e.g. Senior Mortgage Adviser" />
              ) : (
                <span style={{ color: member.job_title ? '#0e1b2c' : '#a0aec0', fontSize: 14 }}>
                  {member.job_title || 'Not set'}
                </span>
              )}
            </Section>

            {/* Bio */}
            <Section icon={<User size={14} />} label="Bio">
              {editing ? (
                <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  placeholder="A short bio…" />
              ) : (
                <span style={{ color: member.bio ? '#0e1b2c' : '#a0aec0', fontSize: 14, whiteSpace: 'pre-wrap' }}>
                  {member.bio || 'Not set'}
                </span>
              )}
            </Section>

            <Divider />

            {/* Contact */}
            <Section icon={<Mail size={14} />} label="Email">
              {editing ? (
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  style={inputStyle} placeholder="email@example.com" />
              ) : (
                <a href={`mailto:${member.email}`} style={{ color: '#10b981', fontSize: 14, textDecoration: 'none' }}>
                  {member.email}
                </a>
              )}
            </Section>

            <Section icon={<Phone size={14} />} label="Phone">
              {editing ? (
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  style={inputStyle} placeholder="+44 7700 000000" />
              ) : (
                <span style={{ color: (member.phone || member.phone_number) ? '#0e1b2c' : '#a0aec0', fontSize: 14 }}>
                  {member.phone || member.phone_number || 'Not set'}
                </span>
              )}
            </Section>

            <Divider />

            {/* Access */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</label>
              <div style={{ marginTop: 6 }}>
                <RoleBadge level={member.broker_level} large />
                <span style={{ fontSize: 12, color: '#a0aec0', marginLeft: 8 }}>Role changes via the table dropdowns</span>
              </div>
            </div>

            {member.manager_name && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Manager</label>
                <div style={{ marginTop: 6, fontSize: 14, color: '#0e1b2c' }}>{member.manager_name}</div>
              </div>
            )}

            <Divider />

            {/* Security */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Shield size={11} /> Security
              </label>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#4a5568' }}>2FA</span>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                    background: member.two_fa_enabled ? '#d1fae5' : '#EDF2F7',
                    color:      member.two_fa_enabled ? '#065f46' : '#a0aec0' }}>
                    {member.two_fa_enabled ? `Enabled${member.two_fa_setup_date ? ' · ' + fmtDate(member.two_fa_setup_date) : ''}` : 'Not enabled'}
                  </span>
                </div>
                {onResetPassword && (
                  <button onClick={() => onResetPassword(member)}
                    style={{ marginTop: 4, padding: '8px 14px', background: '#e0f2fe', color: '#075985',
                      border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      alignSelf: 'flex-start' }}>
                    🔑 Reset Password
                  </button>
                )}
              </div>
            </div>

            <Divider />

            {/* Meta */}
            <Section icon={<Calendar size={14} />} label="Member since">
              <span style={{ fontSize: 14, color: '#4a5568' }}>{fmtDate(member.created_at)}</span>
            </Section>
            {member.updated_at && (
              <Section icon={<Calendar size={14} />} label="Last updated">
                <span style={{ fontSize: 14, color: '#4a5568' }}>{fmtDate(member.updated_at)}</span>
              </Section>
            )}

          </div>
        )}
      </div>
    </>
  );
}

function Section({ icon, label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 4 }}>
        {icon} {label}
      </label>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: '1px solid #EDF2F7', margin: '16px 0' }} />;
}

function StatusBadge({ status }) {
  const active = status === 'active';
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 99, marginRight: 6,
      background: active ? '#d1fae5' : '#fee2e2', color: active ? '#065f46' : '#991b1b' }}>
      {status || 'active'}
    </span>
  );
}

function RoleBadge({ level, large }) {
  const colours = { owner: ['#fef3c7','#92400e'], admin: ['#ede9fe','#5b21b6'], manager: ['#e0f2fe','#075985'], adviser: ['#EDF2F7','#4a5568'] };
  const [bg, fg] = colours[level] || colours.adviser;
  return (
    <span style={{ fontSize: large ? 12 : 11, fontWeight: 600, padding: large ? '3px 9px' : '2px 7px',
      borderRadius: 99, background: bg, color: fg }}>
      {level ? level.charAt(0).toUpperCase() + level.slice(1) : 'Adviser'}
    </span>
  );
}

function PanelBtn({ onClick, bg, color, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: '6px 12px', background: bg, color, border: 'none', borderRadius: 7,
        cursor: disabled ? 'default' : 'pointer', fontSize: 13, fontWeight: 600,
        display: 'inline-flex', alignItems: 'center', opacity: disabled ? 0.7 : 1 }}>
      {children}
    </button>
  );
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 11px',
  border: '1px solid #E2E8F0', borderRadius: 7, fontSize: 14, outline: 'none',
};
